/**
 * `normalizeProgressPct` — THE single seam for rendering
 * `ProgressFrame.pct` (harn-sdk `contract.ts:56`), which today is a bare
 * `number` with NO ratified unit: 8 producer sites emit 0-100 (all
 * `planner`/`reviser`, one lane's, aligning on ratification) while 119
 * emit 0-1 fractions. Before this seam LexIDE read the field SIX ways in
 * THREE behaviors — `pct * 100` (one pane: 75 rendered "7500%"), raw/
 * clamped (four panes: 0.75 rendered "1%"), and one deliberately tolerant
 * normalizer in `HarnessProgressBlock`. This function is that sixth
 * site's shape, extracted verbatim as the in-tree precedent
 * (bus thread `…-q0q1`, handshake with `harn-planner-reviser`).
 *
 * Scale rule: values in (1, ∞) are 0-100 (fractions cannot exceed 1);
 * values in [0, 1] are fractions ×100. The `1.0` boundary reads as
 * fraction-COMPLETE (100%) — matching the producer census (119:8) and the
 * shipped sixth-pane semantics; the opposite reading (a 0-100 producer
 * emitting integer 1) has ZERO instances in the fleet today (measured by
 * `harn-planner-reviser` across every producer, thread `…-rcg5`) and they
 * committed to keeping their producers off the ambiguous point.
 *
 * ⚖ RATIFIED 2026-08-21 (`45791de3b`): pct is a FRACTION in [0,1],
 * documented at the field — decided ON this normalizer's tie-break (`1`
 * must read as done; only the fraction ruling makes a shipped consumer
 * correct there). ⚠ THIS TOLERANT FORM IS A TRANSITION, not the
 * contract: once the 8 remaining 0-100 producer sites (planner/reviser)
 * land and ROLL, the scale line below collapses to `pct * 100` — do not
 * let the shim become the spec, and do not add per-site conversions
 * back; that is the defect this file removes.
 *
 * Returns an integer 0-100, or null when the input cannot be a
 * percentage (non-finite) — callers keep their existing absent-handling.
 */
export function normalizeProgressPct(raw: number | null | undefined): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;
  const scaled = raw > 1 ? raw : raw * 100;
  return Math.max(0, Math.min(100, Math.round(scaled)));
}
